import PropTypes from "prop-types";
import React, { useEffect, useRef, useState } from "react";
import styled from "styled-components";
import Breadcrumbs from "../based/Breadcrumbs";
import CommonTable from "../based/CommonTable";
import { INITPAGING, NOTIFY } from "../based/Constants";
import { ConfirmDialog } from "../based/Dialog";
import Loading from "../based/Loading";
import CommonModal from "../based/Modal";
import { Notify } from "../based/Notify";
import Request from "../based/Request";
import EmbroideryServices from "../based/services/EmbroideryServices";

// Khớp enum JobStatus dùng chung của backend (0 Queue/1 Processing/2 Done/3 Fail).
const ANALYZE_STATUS = {
    Queue: 0,
    Processing: 1,
    Done: 2,
    Fail: 3,
};

const ANALYZE_STATUS_OPTIONS = [
    { value: ANALYZE_STATUS.Queue, label: "Hàng đợi", className: "badge-secondary" },
    { value: ANALYZE_STATUS.Processing, label: "Đang phân tích", className: "badge-warning" },
    { value: ANALYZE_STATUS.Done, label: "Hoàn thành", className: "badge-success" },
    { value: ANALYZE_STATUS.Fail, label: "Thất bại", className: "badge-danger" },
];

// Cách worker tách ký tự trong file alphabet DST, khớp DstAlphabetAnalysisOptions.CharacterSplitMode.
const SPLIT_MODE_OPTIONS = [
    { value: "Stop", label: "Theo lệnh STOP / COLOR_CHANGE" },
    { value: "Trim", label: "Theo chuỗi jump dài (TRIM)" },
];

const UNDERLAY_TYPE_OPTIONS = [
    { value: "Zigzag", label: "Zigzag" },
    { value: "Tatami", label: "Tatami" },
    { value: "Double Tatami", label: "Double Tatami" },
    { value: "Center Walk", label: "Center Walk" },
    { value: "Edge Walk", label: "Edge Walk" },
];

const DEFAULT_PAGING = { ...INITPAGING, pageSize: 20 };
// Job chạy nền nên trong lúc còn dòng Queue/Processing thì tự refresh cho admin thấy tiến trình.
const AUTO_REFRESH_MS = 10000;

function createEmptyAlphabetFile() {
    return {
        id: 0,
        name: "",
        dstFileUrl: "",
        dstFileName: "",
        characterTextFileUrl: "",
        characterTextFileName: "",
        fontName: "",
        fontCode: "",
        sizeMm: 20,
        characterSplitMode: "Stop",
        underlayProfileEnabled: true,
        firstUnderlayEnabled: true,
        firstUnderlayType: "Zigzag",
        firstUnderlaySpacingMm: 3,
        firstUnderlayLengthMm: 4,
        firstUnderlayAngleDeg: 90,
        firstUnderlayMarginMm: 0.2,
        secondUnderlayEnabled: false,
        secondUnderlayType: "Double Tatami",
        secondUnderlaySpacingMm: 3,
        secondUnderlayLengthMm: 4,
        secondUnderlayAngleDeg: 45,
        secondUnderlayMarginMm: 0.2,
        status: ANALYZE_STATUS.Queue,
        message: "",
        characterCount: 0,
        reviewRequiredCount: 0,
        persistedGlyphCount: 0,
        fontId: 0,
        outputDirectory: "",
        analyzedDate: null,
        isActive: true,
        sortOrder: 0,
    };
}

function getStatusOption(status) {
    return ANALYZE_STATUS_OPTIONS.find((x) => x.value == status) || ANALYZE_STATUS_OPTIONS[0];
}

function formatDateTime(value) {
    if (!value) return "";
    const date = new Date(value);
    if (Number.isNaN(date.getTime())) return "";
    return date.toLocaleString("vi-VN");
}

// Màn quản lý file alphabet DST: admin upload file DST + file .txt liệt kê ký tự, backend tạo job,
// worker App.ServicesUV (profile AnalyzeDstAlphabet) tách ký tự, phân tích underlay/fill rồi tạo bộ font thêu.
function toNullableNumber(value) {
    if (value === "" || value === null || value === undefined) return null;
    const parsed = Number(value);
    return Number.isFinite(parsed) ? parsed : null;
}

function normalizeAlphabetFileForSave(model) {
    const profileEnabled = model.underlayProfileEnabled === true;
    const firstEnabled = profileEnabled && model.firstUnderlayEnabled === true;
    const secondEnabled = profileEnabled && model.secondUnderlayEnabled === true;
    return {
        ...model,
        fontName: model.fontName || model.name,
        sizeMm: model.sizeMm > 0 ? model.sizeMm : 20,
        sortOrder: model.sortOrder || 0,
        isActive: model.isActive !== false,
        underlayProfileEnabled: profileEnabled,
        firstUnderlayEnabled: firstEnabled,
        firstUnderlayType: firstEnabled ? model.firstUnderlayType : "",
        firstUnderlaySpacingMm: firstEnabled ? toNullableNumber(model.firstUnderlaySpacingMm) : null,
        firstUnderlayLengthMm: firstEnabled ? toNullableNumber(model.firstUnderlayLengthMm) : null,
        firstUnderlayAngleDeg: firstEnabled ? toNullableNumber(model.firstUnderlayAngleDeg) : null,
        firstUnderlayMarginMm: firstEnabled ? toNullableNumber(model.firstUnderlayMarginMm) : null,
        secondUnderlayEnabled: secondEnabled,
        secondUnderlayType: secondEnabled ? model.secondUnderlayType : "",
        secondUnderlaySpacingMm: secondEnabled ? toNullableNumber(model.secondUnderlaySpacingMm) : null,
        secondUnderlayLengthMm: secondEnabled ? toNullableNumber(model.secondUnderlayLengthMm) : null,
        secondUnderlayAngleDeg: secondEnabled ? toNullableNumber(model.secondUnderlayAngleDeg) : null,
        secondUnderlayMarginMm: secondEnabled ? toNullableNumber(model.secondUnderlayMarginMm) : null,
    };
}

function isAnalyzeFieldChanged(current, original) {
    if (!original || original.id <= 0) return false;
    const next = normalizeAlphabetFileForSave(current);
    const prev = normalizeAlphabetFileForSave({ ...createEmptyAlphabetFile(), ...original });
    return [
        "dstFileUrl",
        "characterTextFileUrl",
        "characterSplitMode",
        "underlayProfileEnabled",
        "firstUnderlayEnabled",
        "firstUnderlayType",
        "firstUnderlaySpacingMm",
        "firstUnderlayLengthMm",
        "firstUnderlayAngleDeg",
        "firstUnderlayMarginMm",
        "secondUnderlayEnabled",
        "secondUnderlayType",
        "secondUnderlaySpacingMm",
        "secondUnderlayLengthMm",
        "secondUnderlayAngleDeg",
        "secondUnderlayMarginMm",
    ].some((field) => (next[field] ?? "") != (prev[field] ?? ""));
}

function EmbroideryAlphabetManagement() {
    const [datas, setDatas] = useState([]);
    const [paging, setPaging] = useState(DEFAULT_PAGING);
    const [editingModel, setEditingModel] = useState(null);
    const [isShowModal, setIsShowModal] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [loadingMsg, setLoadingMsg] = useState("Đang tải...");
    const pagingRef = useRef(DEFAULT_PAGING);

    useEffect(() => {
        _getData(DEFAULT_PAGING);
    }, []);

    // Còn dòng đang chờ/đang chạy thì hẹn refresh lại, xong hết thì dừng để khỏi gọi API vô ích.
    useEffect(() => {
        const hasRunning = datas.some((x) => x.status == ANALYZE_STATUS.Queue || x.status == ANALYZE_STATUS.Processing);
        if (!hasRunning) return undefined;
        const timer = setTimeout(() => _getData(pagingRef.current, true), AUTO_REFRESH_MS);
        return () => clearTimeout(timer);
    }, [datas]);

    async function _getData(newPaging, isSilent) {
        const requestPaging = newPaging || pagingRef.current || DEFAULT_PAGING;
        if (!isSilent) {
            setIsLoading(true);
            setLoadingMsg("Đang tải danh sách file alphabet...");
        }
        const [err, data] = await EmbroideryServices.GetAlphabetFiles(requestPaging);
        if (!err && data) {
            setDatas(data.listObjects || []);
            const responsePaging = data.paging || requestPaging;
            pagingRef.current = responsePaging;
            setPaging(responsePaging);
        } else if (!isSilent) {
            setDatas([]);
            pagingRef.current = DEFAULT_PAGING;
            setPaging(DEFAULT_PAGING);
            Notify(NOTIFY.WARNING, "Có lỗi", "Không tải được danh sách file alphabet.");
        }
        if (!isSilent) setIsLoading(false);
    }

    function _openCreateModal() {
        setEditingModel(createEmptyAlphabetFile());
        setIsShowModal(true);
    }

    async function _openEditModal(item) {
        setIsLoading(true);
        setLoadingMsg("Đang tải thông tin file alphabet...");
        const [err, data] = await EmbroideryServices.GetAlphabetFileById(item.id);
        setIsLoading(false);
        if (!err && data) {
            setEditingModel({ ...createEmptyAlphabetFile(), ...data });
            setIsShowModal(true);
        } else {
            Notify(NOTIFY.WARNING, "Có lỗi", "Không tải được thông tin file alphabet.");
        }
    }

    function _handleCloseModal() {
        setIsShowModal(false);
        setEditingModel(null);
    }

    async function _handleSave(model) {
        setIsLoading(true);
        setLoadingMsg("Đang lưu file alphabet...");
        const [err, data] = await EmbroideryServices.CreateUpdateAlphabetFile(normalizeAlphabetFileForSave(model));
        setIsLoading(false);
        if (err || !data) {
            Notify(NOTIFY.WARNING, "Có lỗi", err && err.message ? err.message : "Không lưu được file alphabet.");
            return;
        }
        Notify(NOTIFY.SUCCESS, "Thành công", "Đã lưu. Worker sẽ phân tích file DST trong vài phút tới.");
        _handleCloseModal();
        _getData(pagingRef.current);
    }

    function _handleRetry(item) {
        ConfirmDialog("Chạy lại phân tích?", `Toàn bộ ký tự của "${item.name}" sẽ được phân tích lại và ghi lại bộ font thêu.`, () => {
            return new Promise(async (resolve, reject) => {
                setIsLoading(true);
                setLoadingMsg("Đang đưa file alphabet vào hàng đợi...");
                const [err, data] = await EmbroideryServices.RetryAnalyzeAlphabetFile(item.id);
                setIsLoading(false);
                if (err || !data) {
                    reject({ title: "Thất bại", msg: "Không tạo được job phân tích." });
                    return;
                }
                _getData(pagingRef.current);
                resolve({ title: "Thành công", msg: "Đã đưa vào hàng đợi phân tích." });
            });
        });
    }

    function _handleDelete(item) {
        ConfirmDialog("Xác nhận xóa?", `Xin vui lòng xác nhận để xóa file alphabet "${item.name}"`, () => {
            return new Promise(async (resolve, reject) => {
                setIsLoading(true);
                setLoadingMsg("Đang xóa file alphabet...");
                const [err, data] = await EmbroideryServices.DeleteAlphabetFile(item.id);
                setIsLoading(false);
                if (err || !data) {
                    reject({ title: "Thất bại", msg: "Vui lòng thử lại sau." });
                    return;
                }
                _getData(pagingRef.current);
                resolve({ title: "Thành công", msg: "Xóa thành công." });
            });
        });
    }

    function _renderAction() {
        return (
            <div className="mb-2 d-flex align-items-center">
                <button className="btn btn-custom" onClick={_openCreateModal}>
                    Upload file alphabet +
                </button>
                <button className="btn btn-default ml-2" onClick={() => _getData(pagingRef.current)}>
                    Làm mới
                </button>
            </div>
        );
    }

    function _renderHeader() {
        return (
            <thead>
                <tr className="text-center">
                    <th style={{ minWidth: 180 }}>Tên bộ chữ</th>
                    <th style={{ minWidth: 200 }}>File DST / bộ ký tự</th>
                    <th style={{ width: 90 }}>Size</th>
                    <th style={{ minWidth: 150 }}>Cách tách ký tự</th>
                    <th style={{ minWidth: 130 }}>Trạng thái</th>
                    <th style={{ minWidth: 170 }}>Kết quả phân tích</th>
                    <th style={{ minWidth: 150 }}>File ký tự đã cắt</th>
                    <th style={{ minWidth: 150 }}>Phân tích lúc</th>
                    <th style={{ width: 210 }}>Thao tác</th>
                </tr>
            </thead>
        );
    }

    function _renderBody() {
        if (!datas || datas.length == 0) {
            return (
                <tbody>
                    <tr>
                        <td colSpan={9} className="text-center">
                            Không có dữ liệu
                        </td>
                    </tr>
                </tbody>
            );
        }
        return (
            <tbody>
                {datas.map((item) => {
                    const statusOption = getStatusOption(item.status);
                    const splitMode = SPLIT_MODE_OPTIONS.find((x) => x.value == item.characterSplitMode);
                    return (
                        <tr key={item.id}>
                            <td className="vertical-align-middle">
                                <div className="font-weight-bold">{item.name}</div>
                                {item.fontCode ? <div className="text-muted font-13">{item.fontCode}</div> : null}
                            </td>
                            <td className="vertical-align-middle">
                                <div>
                                    {item.dstFileUrl ? (
                                        <a href={item.dstFileUrl} target="_blank" rel="noopener noreferrer">
                                            {item.dstFileName || "file.dst"}
                                        </a>
                                    ) : (
                                        <span className="text-danger">Chưa có file DST</span>
                                    )}
                                </div>
                                <div>
                                    {item.characterTextFileUrl ? (
                                        <a href={item.characterTextFileUrl} target="_blank" rel="noopener noreferrer" className="font-13">
                                            {item.characterTextFileName || "alphabet.txt"}
                                        </a>
                                    ) : (
                                        <span className="text-muted font-13">Chưa có file .txt bộ ký tự</span>
                                    )}
                                </div>
                            </td>
                            <td className="text-center vertical-align-middle">{item.sizeMm || 0} mm</td>
                            <td className="text-center vertical-align-middle">{splitMode ? splitMode.label : item.characterSplitMode}</td>
                            <td className="text-center vertical-align-middle">
                                <span className={`badge ${statusOption.className}`}>{statusOption.label}</span>
                                {item.message ? <div className="status-message font-13 text-muted mt-1">{item.message}</div> : null}
                            </td>
                            <td className="vertical-align-middle font-13">
                                {item.status == ANALYZE_STATUS.Done ? (
                                    <React.Fragment>
                                        <div>
                                            {item.characterCount} ký tự / {item.persistedGlyphCount} glyph đã ghi
                                        </div>
                                        <div className={item.reviewRequiredCount > 0 ? "text-warning" : "text-muted"}>{item.reviewRequiredCount} ký tự cần review</div>
                                        {item.fontId > 0 ? <div className="text-muted">EMB_Fonts Id: {item.fontId}</div> : null}
                                    </React.Fragment>
                                ) : (
                                    <span className="text-muted">--</span>
                                )}
                            </td>
                            <td className="text-center vertical-align-middle font-13">
                                {item.charactersZipUrl ? (
                                    <a className="btn btn-default btn-sm" href={item.charactersZipUrl} download={item.charactersZipFileName || ""} target="_blank" rel="noopener noreferrer">
                                        <i className="fa fa-download mr-1" />
                                        Tải ZIP
                                    </a>
                                ) : (
                                    <span className="text-muted">--</span>
                                )}
                            </td>
                            <td className="text-center vertical-align-middle font-13">{formatDateTime(item.analyzedDate)}</td>
                            <td className="text-center vertical-align-middle">
                                <button className="btn btn-custom m-1" onClick={() => _openEditModal(item)}>
                                    Sửa
                                </button>
                                <button className="btn btn-default m-1" disabled={item.status == ANALYZE_STATUS.Queue || item.status == ANALYZE_STATUS.Processing} onClick={() => _handleRetry(item)}>
                                    Chạy lại
                                </button>
                                <button className="btn btn-danger m-1" onClick={() => _handleDelete(item)}>
                                    Xóa
                                </button>
                            </td>
                        </tr>
                    );
                })}
            </tbody>
        );
    }

    return (
        <React.Fragment>
            <Breadcrumbs currentPage="Quản lý file alphabet thêu" />
            <Loading show={isLoading} msg={loadingMsg} />
            <Wrapper>
                <CommonTable data={datas} paging={paging} searchBasic placeholderSearch="Tên bộ chữ / mã font / tên file" freeSize renderAction={_renderAction} renderHeader={_renderHeader} renderBody={_renderBody} onFilter={_getData} />
            </Wrapper>
            <AlphabetFileModal isShowModal={isShowModal} model={editingModel} onClose={_handleCloseModal} onSave={_handleSave} />
        </React.Fragment>
    );
}

export default EmbroideryAlphabetManagement;

// Modal upload file alphabet: file DST bắt buộc, file .txt liệt kê ký tự theo đúng thứ tự glyph trong DST.
function AlphabetFileModal({ isShowModal, model, onClose, onSave }) {
    const [formModel, setFormModel] = useState(createEmptyAlphabetFile());
    const [isBusy, setIsBusy] = useState(false);
    const [busyMsg, setBusyMsg] = useState("Đang xử lý...");

    useEffect(() => {
        if (isShowModal && model) setFormModel({ ...createEmptyAlphabetFile(), ...model });
    }, [isShowModal, model]);

    const isValid = formModel.name && formModel.dstFileUrl;
    const isAnalyzeInputChanged = isAnalyzeFieldChanged(formModel, model);

    function _handleChange(field, value) {
        setFormModel((prev) => ({ ...prev, [field]: value }));
    }

    function _handleNumberChange(field, value) {
        setFormModel((prev) => ({ ...prev, [field]: value === "" ? "" : Number(value) }));
    }

    // Upload file lên CDN rồi chỉ giữ url + tên file trong model, backend không nhận stream file.
    async function _handleUpload(e, extension, urlField, nameField) {
        const input = e.target;
        const file = input.files[0];
        input.value = "";
        if (!file) return;
        if (!file.name.toLowerCase().endsWith(extension)) {
            Notify(NOTIFY.WARNING, "Sai định dạng", `Vui lòng chọn file ${extension}`);
            return;
        }
        setBusyMsg(`Đang upload ${file.name}...`);
        setIsBusy(true);
        let res = null;
        try {
            res = await Request.UploadDocument(file);
        } catch (ex) {
            res = null;
        }
        setIsBusy(false);
        if (res && res.data && res.data.url) {
            setFormModel((prev) => ({ ...prev, [urlField]: res.data.url, [nameField]: file.name }));
        } else {
            Notify(NOTIFY.ERROR, NOTIFY.ERROR, `Upload file ${extension} thất bại`);
        }
    }

    function _handleSave() {
        if (!isValid) {
            Notify(NOTIFY.WARNING, "Thiếu thông tin", "Vui lòng nhập tên bộ chữ và upload file DST.");
            return;
        }
        onSave(formModel);
    }

    return (
        <CommonModal
            show={isShowModal}
            id="embroidery-alphabet-modal"
            size="lg"
            isBusy={isBusy}
            busyMsg={busyMsg}
            title={formModel.id > 0 ? "Sửa file alphabet" : "Upload file alphabet"}
            onSave={_handleSave}
            onClose={onClose}
        >
            <ModalWrapper>
                <div className="row">
                    <div className="col-6 form-group">
                        <label className="required">Tên bộ chữ</label>
                        <input type="text" className="form-control" value={formModel.name} onChange={(e) => _handleChange("name", e.target.value)} placeholder="VD: Roboto DST Alphabet" />
                    </div>
                    <div className="col-6 form-group">
                        <label>Tên font thêu sẽ tạo</label>
                        <input type="text" className="form-control" value={formModel.fontName} onChange={(e) => _handleChange("fontName", e.target.value)} placeholder="Để trống sẽ lấy theo tên bộ chữ" />
                    </div>
                </div>
                <div className="row">
                    <div className="col-6 form-group">
                        <label>Mã font (Code)</label>
                        <input type="text" className="form-control" value={formModel.fontCode} onChange={(e) => _handleChange("fontCode", e.target.value)} placeholder="Để trống sẽ tự sinh từ tên font" />
                    </div>
                    <div className="col-3 form-group">
                        <label>Chiều cao chữ</label>
                        <div className="unit-input">
                            <input type="number" className="form-control" value={formModel.sizeMm} onChange={(e) => _handleChange("sizeMm", Number(e.target.value))} />
                            <span>mm</span>
                        </div>
                    </div>
                    <div className="col-3 form-group">
                        <label>Thứ tự</label>
                        <input type="number" className="form-control" value={formModel.sortOrder} onChange={(e) => _handleChange("sortOrder", Number(e.target.value))} />
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 form-group">
                        <label>Cách tách ký tự trong file DST</label>
                        <select className="form-control" value={formModel.characterSplitMode} onChange={(e) => _handleChange("characterSplitMode", e.target.value)}>
                            {SPLIT_MODE_OPTIONS.map((option) => (
                                <option key={option.value} value={option.value}>
                                    {option.label}
                                </option>
                            ))}
                        </select>
                    </div>
                </div>
                <div className="underlay-profile">
                    <label className="d-flex align-items-center mb-2">
                        <input type="checkbox" className="mr-2" checked={formModel.underlayProfileEnabled === true} onChange={(e) => _handleChange("underlayProfileEnabled", e.target.checked)} />
                        Underlay profile heuristic
                    </label>
                    <div className={formModel.underlayProfileEnabled === true ? "" : "profile-disabled"}>
                        <div className="profile-row-title">
                            <label className="d-flex align-items-center mb-0">
                                <input type="checkbox" className="mr-2" checked={formModel.firstUnderlayEnabled === true} disabled={formModel.underlayProfileEnabled !== true} onChange={(e) => _handleChange("firstUnderlayEnabled", e.target.checked)} />
                                First underlay
                            </label>
                        </div>
                        <div className="row">
                            <div className="col-md-4 form-group">
                                <label>Type</label>
                                <select className="form-control" value={formModel.firstUnderlayType || "Zigzag"} disabled={formModel.underlayProfileEnabled !== true || formModel.firstUnderlayEnabled !== true} onChange={(e) => _handleChange("firstUnderlayType", e.target.value)}>
                                    {UNDERLAY_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Spacing</label>
                                <div className="unit-input">
                                    <input type="number" step="0.01" className="form-control" value={formModel.firstUnderlaySpacingMm ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.firstUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("firstUnderlaySpacingMm", e.target.value)} />
                                    <span>mm</span>
                                </div>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Length</label>
                                <div className="unit-input">
                                    <input type="number" step="0.01" className="form-control" value={formModel.firstUnderlayLengthMm ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.firstUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("firstUnderlayLengthMm", e.target.value)} />
                                    <span>mm</span>
                                </div>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Angle</label>
                                <div className="unit-input">
                                    <input type="number" step="1" className="form-control" value={formModel.firstUnderlayAngleDeg ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.firstUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("firstUnderlayAngleDeg", e.target.value)} />
                                    <span>deg</span>
                                </div>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Margin</label>
                                <div className="unit-input">
                                    <input type="number" step="0.01" className="form-control" value={formModel.firstUnderlayMarginMm ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.firstUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("firstUnderlayMarginMm", e.target.value)} />
                                    <span>mm</span>
                                </div>
                            </div>
                        </div>
                        <div className="profile-row-title">
                            <label className="d-flex align-items-center mb-0">
                                <input type="checkbox" className="mr-2" checked={formModel.secondUnderlayEnabled === true} disabled={formModel.underlayProfileEnabled !== true} onChange={(e) => _handleChange("secondUnderlayEnabled", e.target.checked)} />
                                Second underlay
                            </label>
                        </div>
                        <div className="row">
                            <div className="col-md-4 form-group">
                                <label>Type</label>
                                <select className="form-control" value={formModel.secondUnderlayType || "Double Tatami"} disabled={formModel.underlayProfileEnabled !== true || formModel.secondUnderlayEnabled !== true} onChange={(e) => _handleChange("secondUnderlayType", e.target.value)}>
                                    {UNDERLAY_TYPE_OPTIONS.map((option) => (
                                        <option key={option.value} value={option.value}>
                                            {option.label}
                                        </option>
                                    ))}
                                </select>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Spacing</label>
                                <div className="unit-input">
                                    <input type="number" step="0.01" className="form-control" value={formModel.secondUnderlaySpacingMm ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.secondUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("secondUnderlaySpacingMm", e.target.value)} />
                                    <span>mm</span>
                                </div>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Length</label>
                                <div className="unit-input">
                                    <input type="number" step="0.01" className="form-control" value={formModel.secondUnderlayLengthMm ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.secondUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("secondUnderlayLengthMm", e.target.value)} />
                                    <span>mm</span>
                                </div>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Angle</label>
                                <div className="unit-input">
                                    <input type="number" step="1" className="form-control" value={formModel.secondUnderlayAngleDeg ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.secondUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("secondUnderlayAngleDeg", e.target.value)} />
                                    <span>deg</span>
                                </div>
                            </div>
                            <div className="col-md-2 form-group">
                                <label>Margin</label>
                                <div className="unit-input">
                                    <input type="number" step="0.01" className="form-control" value={formModel.secondUnderlayMarginMm ?? ""} disabled={formModel.underlayProfileEnabled !== true || formModel.secondUnderlayEnabled !== true} onChange={(e) => _handleNumberChange("secondUnderlayMarginMm", e.target.value)} />
                                    <span>mm</span>
                                </div>
                            </div>
                        </div>
                    </div>
                    <div className="hint">Profile nay duoc worker dung de tang diem run dau neu stitch length, angle, spacing va margin giong cau hinh underlay cua font.</div>
                </div>
                <div className="row">
                    <div className="col-6 form-group">
                        <label className="required">File alphabet (.dst)</label>
                        <div className="file-row">
                            <label className="btn btn-custom mb-0" htmlFor="alphabet-dst-file">
                                Chọn file .DST
                            </label>
                            <input type="file" id="alphabet-dst-file" accept=".dst,.DST" className="d-none" onChange={(e) => _handleUpload(e, ".dst", "dstFileUrl", "dstFileName")} />
                            {formModel.dstFileUrl ? (
                                <a href={formModel.dstFileUrl} target="_blank" rel="noopener noreferrer" className="file-name">
                                    {formModel.dstFileName || "file.dst"}
                                </a>
                            ) : (
                                <span className="file-name text-muted">Chưa chọn file</span>
                            )}
                        </div>
                    </div>
                    <div className="col-6 form-group">
                        <label>File bộ ký tự (.txt)</label>
                        <div className="file-row">
                            <label className="btn btn-custom mb-0" htmlFor="alphabet-txt-file">
                                Chọn file .TXT
                            </label>
                            <input type="file" id="alphabet-txt-file" accept=".txt" className="d-none" onChange={(e) => _handleUpload(e, ".txt", "characterTextFileUrl", "characterTextFileName")} />
                            {formModel.characterTextFileUrl ? (
                                <a href={formModel.characterTextFileUrl} target="_blank" rel="noopener noreferrer" className="file-name">
                                    {formModel.characterTextFileName || "alphabet.txt"}
                                </a>
                            ) : (
                                <span className="file-name text-muted">Chưa chọn file</span>
                            )}
                        </div>
                        <div className="hint">File .txt UTF-8, mỗi dòng một ký tự, đúng thứ tự ký tự xuất hiện trong file DST. Không có file này worker sẽ đặt tên ký tự là glyph_000, glyph_001...</div>
                    </div>
                </div>
                <div className="row">
                    <div className="col-12 form-group">
                        <label className="d-flex align-items-center">
                            <input type="checkbox" className="mr-2" checked={formModel.isActive !== false} onChange={(e) => _handleChange("isActive", e.target.checked)} />
                            Bộ font tạo ra được hiển thị cho khách chọn
                        </label>
                    </div>
                </div>
                {formModel.id > 0 && formModel.message ? <div className="alert alert-secondary mb-2">{formModel.message}</div> : null}
                <div className="alert alert-info mb-0">
                    {formModel.id == 0 || isAnalyzeInputChanged
                        ? "Sau khi lưu, hệ thống tạo job phân tích. Worker sẽ tách ký tự, phân loại underlay/fill rồi tạo bộ font thêu - trạng thái cập nhật trên danh sách."
                        : "Đổi tên/mã font/thứ tự sẽ lưu ngay mà không phân tích lại. Muốn phân tích lại hãy bấm Chạy lại ở danh sách hoặc thay file DST/.txt."}
                </div>
            </ModalWrapper>
        </CommonModal>
    );
}

AlphabetFileModal.propTypes = {
    isShowModal: PropTypes.bool,
    model: PropTypes.object,
    onClose: PropTypes.func,
    onSave: PropTypes.func,
};

const Wrapper = styled.div`
    .status-message {
        max-width: 260px;
        word-break: break-word;
        white-space: pre-wrap;
    }
`;

const ModalWrapper = styled.div`
    label.required:after {
        content: " *";
        color: #d70000;
    }

    .form-control {
        border-radius: 0;
        border-color: #d9d9d9;
        color: #676767;
    }

    .unit-input {
        position: relative;
    }

    .unit-input input {
        padding-right: 48px;
    }

    .unit-input span {
        color: #bebebe;
        position: absolute;
        right: 15px;
        top: 50%;
        transform: translateY(-50%);
        pointer-events: none;
    }

    .file-row {
        display: flex;
        align-items: center;
        gap: 12px;
    }

    .file-row .file-name {
        word-break: break-all;
    }

    .underlay-profile {
        border: 1px solid #e6e6e6;
        padding: 12px 12px 6px;
        margin-bottom: 16px;
        background: #fbfbfb;
    }

    .profile-row-title {
        border-top: 1px solid #ececec;
        padding-top: 10px;
        margin-bottom: 8px;
    }

    .profile-row-title:first-child {
        border-top: 0;
        padding-top: 0;
    }

    .profile-disabled {
        opacity: 0.55;
    }

    .hint {
        color: #9a9a9a;
        font-size: 12px;
        margin-top: 6px;
    }
`;
